import React, { useState, useEffect } from 'react';
import { useAuth } from './keycloak.jsx';
import { makeAuthenticatedRequest } from './api/axiosConfig.js';
import { useLanguage } from './LanguageContext.jsx';

const api = import.meta.env.VITE_API_GATEWAY;

export default function Admin() {
  const { token, roles, isAdmin, keycloak } = useAuth();
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [userId, setUserId] = useState('');
  const [path, setPath] = useState('');
  const [level, setLevel] = useState('read');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [currentToken, setCurrentToken] = useState(token);
  
  // Estados para filtros
  const [filterUser, setFilterUser] = useState('');
  const [filterPath, setFilterPath] = useState('');
  const [filterExpiry, setFilterExpiry] = useState('all'); // 'all', 'expired', 'expiringSoon', 'valid'
  const [sortBy, setSortBy] = useState('user'); // 'user', 'path', 'expiry', 'created'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  
  // Estados para edición inline
  const [editingPermission, setEditingPermission] = useState(null);
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Estados para importación CSV
  const [csvFile, setCsvFile] = useState(null);
  const [csvMode, setCsvMode] = useState('import'); // 'import' o 'delete'
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvSectionOpen, setCsvSectionOpen] = useState(false);

  // Estados para backup/restauración
  const [backupSectionOpen, setBackupSectionOpen] = useState(false);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [backupResult, setBackupResult] = useState(null);
  const [availableBackups, setAvailableBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [extendExpiredPermissions, setExtendExpiredPermissions] = useState(false);
  const [extendDays, setExtendDays] = useState(30);

  // Función para filtrar los permisos
  const filterPermissions = (permissions, userFilter, pathFilter, expiryFilter) => {
    return permissions.filter(permission => {
      const matchesUser = !userFilter || 
        permission.user_id.toLowerCase().includes(userFilter.toLowerCase());
      const matchesPath = !pathFilter || 
        permission.path.toLowerCase().includes(pathFilter.toLowerCase());
      
      // Filtrar por estado de caducidad
      let matchesExpiry = true;
      if (expiryFilter !== 'all') {
        const isExpired = permission.expiry_date && new Date(permission.expiry_date) < new Date();
        const isExpiringSoon = permission.expiry_date && !isExpired && 
          new Date(permission.expiry_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        
        switch (expiryFilter) {
          case 'expired':
            matchesExpiry = isExpired;
            break;
          case 'expiringSoon':
            matchesExpiry = isExpiringSoon;
            break;
          case 'valid':
            matchesExpiry = !isExpired && !isExpiringSoon;
            break;
          default:
            matchesExpiry = true;
        }
      }
      
      return matchesUser && matchesPath && matchesExpiry;
    });
  };

  // Función para ordenar los permisos
  const sortPermissions = (permissions, sortBy, sortOrder) => {
    return [...permissions].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'user':
          valueA = a.user_id.toLowerCase();
          valueB = b.user_id.toLowerCase();
          break;
        case 'path':
          valueA = a.path.toLowerCase();
          valueB = b.path.toLowerCase();
          break;
        case 'expiry':
          valueA = a.expiry_date ? new Date(a.expiry_date) : new Date('9999-12-31');
          valueB = b.expiry_date ? new Date(b.expiry_date) : new Date('9999-12-31');
          break;
        case 'created':
          valueA = a.created_date ? new Date(a.created_date) : new Date('1970-01-01');
          valueB = b.created_date ? new Date(b.created_date) : new Date('1970-01-01');
          break;
        default:
          return 0;
      }
      
      if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Aplicar filtros y ordenamiento a los permisos
  const filteredAndSortedEntries = sortPermissions(
    filterPermissions(entries, filterUser, filterPath, filterExpiry),
    sortBy,
    sortOrder
  );

  // Función para manejar peticiones con renovación automática de token
  const makeAuthenticatedRequestWithTokenUpdate = async (config, retries = 2) => {
    try {
      return await makeAuthenticatedRequest(config, currentToken, retries);
    } catch (error) {
      if (error.response?.status === 401 && retries > 0) {
        console.log('🔄 Token expirado, renovando...');
        try {
          await keycloak.updateToken(30);
          setCurrentToken(keycloak.token);
          return await makeAuthenticatedRequest(config, keycloak.token, 0);
        } catch (renewError) {
          console.error('❌ Error renovando token:', renewError);
          throw error;
        }
      }
      throw error;
    }
  };

  // Sincronizar token cuando cambie
  useEffect(() => {
    setCurrentToken(token);
  }, [token]);

  const loadPermissions = async () => {
    try {
      const response = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}/api?action=list_permissions`,
        headers: { 'Content-Type': 'application/json' }
      });
      setEntries(response.data.items || []);
    } catch (error) {
      console.error('❌ Error cargando permisos:', error);
      setEntries([]); // Establecer array vacío en caso de error
    }
  };

  const savePermission = async () => {
    try {
      const permissionData = {
        user_id: userId,
        path,
        level
      };

      // Añadir fecha de caducidad si se especifica
      if (expiryDate) {
        permissionData.expiry_date = expiryDate;
      }

      // Añadir notas si se especifican
      if (notes.trim()) {
        permissionData.notes = notes.trim();
      }

      await makeAuthenticatedRequestWithTokenUpdate({
        method: 'POST',
        url: `${api}/api?action=update_permission`,
        data: permissionData,
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Limpiar formulario después de guardar
      setUserId('');
      setPath('');
      setLevel('read');
      setExpiryDate('');
      setNotes('');
      
      loadPermissions(); // Recargar después de guardar
    } catch (error) {
      console.error('❌ Error guardando permiso:', error);
    }
  };

  const deletePermission = async (userIdToDelete, pathToDelete) => {
    try {
      await makeAuthenticatedRequestWithTokenUpdate({
        method: 'POST',
        url: `${api}/api?action=delete_permission`,
        data: {
          user_id: userIdToDelete,
          path: pathToDelete
        },
        headers: { 'Content-Type': 'application/json' }
      });
      
      loadPermissions(); // Recargar después de eliminar
    } catch (error) {
      console.error('❌ Error eliminando permiso:', error);
    }
  };

  // Función para editar un permiso existente
  const editPermission = async (userId, path, newExpiryDate, newNotes) => {
    try {
      const permissionData = {
        user_id: userId,
        path: path,
        level: entries.find(e => e.user_id === userId && e.path === path)?.level || 'read'
      };

      // Añadir fecha de caducidad
      if (newExpiryDate) {
        permissionData.expiry_date = newExpiryDate;
      }

      // Añadir notas
      if (newNotes && newNotes.trim()) {
        permissionData.notes = newNotes.trim();
      }

      await makeAuthenticatedRequestWithTokenUpdate({
        method: 'POST',
        url: `${api}/api?action=update_permission`,
        data: permissionData,
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Limpiar estado de edición
      setEditingPermission(null);
      setEditExpiryDate('');
      setEditNotes('');
      
      loadPermissions(); // Recargar después de editar
    } catch (error) {
      console.error('❌ Error editando permiso:', error);
    }
  };

  // Función para iniciar edición de un permiso
  const startEditPermission = (permission) => {
    setEditingPermission(`${permission.user_id}-${permission.path}`);
    setEditExpiryDate(permission.expiry_date ? permission.expiry_date.split('T')[0] : '');
    setEditNotes(permission.notes || '');
  };

  // Función para cancelar edición
  const cancelEdit = () => {
    setEditingPermission(null);
    setEditExpiryDate('');
    setEditNotes('');
  };

  // ===== FUNCIONES PARA IMPORTACIÓN CSV =====
  
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    setCsvFile(file);
    setCsvResult(null);
  };

  const parseCsvContent = (csvContent) => {
    const lines = csvContent.split('\n').filter(line => line.trim());
    const data = [];
    
    // Saltar la primera línea si es un encabezado
    const startIndex = lines[0] && (
      lines[0].toLowerCase().includes('usuario') || 
      lines[0].toLowerCase().includes('user') ||
      lines[0].toLowerCase().includes('bucket')
    ) ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Parsear CSV considerando que puede haber comas dentro de comillas
      const columns = [];
      let current = '';
      let inQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          columns.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      columns.push(current.trim());
      
      // Limpiar comillas de los valores
      const cleanColumns = columns.map(col => col.replace(/^"(.*)"$/, '$1'));
      
      if (cleanColumns.length >= 3) {
        const [usuario, bucketPath, permiso, fechaCaducidad, notas] = cleanColumns;
        
        if (usuario && bucketPath && permiso) {
          // Normalizar la ruta añadiendo "/" al principio si no la tiene
          let normalizedPath = bucketPath.trim();
          if (!normalizedPath.startsWith('/')) {
            normalizedPath = '/' + normalizedPath;
          }
          
          data.push({
            user_id: usuario.trim(),
            path: normalizedPath,
            level: permiso.trim().toLowerCase(),
            expiry_date: fechaCaducidad && fechaCaducidad.trim() ? fechaCaducidad.trim() : null,
            notes: notas && notas.trim() ? notas.trim() : null
          });
        }
      }
    }
    
    return data;
  };

  const processCsvData = async (csvData, mode) => {
    const results = {
      processed: 0,
      success: 0,
      errors: []
    };
    
    for (const item of csvData) {
      results.processed++;
      
      try {
        if (mode === 'import') {
          // Importar permisos
          const permissionData = {
            user_id: item.user_id,
            path: item.path,
            level: item.level
          };
          
          if (item.expiry_date) {
            permissionData.expiry_date = item.expiry_date;
          }
          
          if (item.notes) {
            permissionData.notes = item.notes;
          }
          
          await makeAuthenticatedRequestWithTokenUpdate({
            method: 'POST',
            url: `${api}/api?action=update_permission`,
            data: permissionData,
            headers: { 'Content-Type': 'application/json' }
          });
          
        } else if (mode === 'delete') {
          // Eliminar permisos
          await makeAuthenticatedRequestWithTokenUpdate({
            method: 'POST',
            url: `${api}/api?action=delete_permission`,
            data: {
              user_id: item.user_id,
              path: item.path
            },
            headers: { 'Content-Type': 'application/json' }
          });
        }
        
        results.success++;
      } catch (error) {
        results.errors.push({
          user: item.user_id,
          path: item.path,
          error: error.message || 'Error desconocido'
        });
      }
    }
    
    return results;
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      alert('Por favor selecciona un archivo CSV');
      return;
    }
    
    setCsvImporting(true);
    setCsvResult(null);
    
    try {
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(csvFile);
      });
      
      const csvData = parseCsvContent(fileContent);
      
      if (csvData.length === 0) {
        setCsvResult({
          processed: 0,
          success: 0,
          errors: [{ error: 'No se encontraron datos válidos en el archivo CSV' }]
        });
        return;
      }
      
      const results = await processCsvData(csvData, csvMode);
      setCsvResult(results);
      
      // Recargar permisos después de la importación
      loadPermissions();
      
    } catch (error) {
      setCsvResult({
        processed: 0,
        success: 0,
        errors: [{ error: `Error procesando archivo: ${error.message}` }]
      });
    } finally {
      setCsvImporting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent = `usuario,bucket/path,permiso,fecha_caducidad,notas
usuario1@uoc.edu,carpeta1/,read,2024-12-31,Acceso temporal
usuario2@uoc.edu,/carpeta2/archivo.pdf,write,,Acceso completo
usuario3@uoc.edu,datos/,read,2024-06-30,Solo lectura`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla_permisos.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ===== FIN FUNCIONES CSV =====

  // ===== FUNCIONES PARA BACKUP Y RESTAURACIÓN =====
  
  const createBackup = async () => {
    setBackupInProgress(true);
    setBackupResult(null);
    
    try {
      // 1. Obtener todos los permisos actuales
      const response = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}/api?action=list_permissions`,
        headers: { 'Content-Type': 'application/json' }
      });
      
      const permissions = response.data.items || [];
      
      if (permissions.length === 0) {
        setBackupResult({
          success: false,
          message: t('admin.backup.noPermissions')
        });
        return;
      }
      
      // 2. Generar contenido CSV con escapado avanzado
      const csvHeader = 'usuario,bucket/path,permiso,fecha_caducidad,notas\n';
      const csvRows = permissions.map(permission => {
        const user = permission.user_id || '';
        const path = permission.path || '';
        const level = permission.level || '';
        const expiry = permission.expiry_date || '';
        const notes = permission.notes || '';
        
        // Función mejorada para escapar campos CSV
        const escapeCSVField = (field) => {
          const fieldStr = String(field);
          // Si el campo contiene comas, comillas dobles, saltos de línea o espacios al inicio/final
          if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n') || fieldStr.includes('\r') || fieldStr !== fieldStr.trim()) {
            // Escapar comillas dobles duplicándolas y envolver en comillas
            return `"${fieldStr.replace(/"/g, '""')}"`;
          }
          return fieldStr;
        };
        
        return `${escapeCSVField(user)},${escapeCSVField(path)},${escapeCSVField(level)},${escapeCSVField(expiry)},${escapeCSVField(notes)}`;
      }).join('\n');
      
      const csvContent = csvHeader + csvRows;
      
      // 3. Generar nombre de archivo con timestamp
      const now = new Date();
      const timestamp = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timepart = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS
      const filename = `backup_permisos_${timestamp}_${timepart}.csv`;
      const backupPath = `backup/${timestamp}/${filename}`;
      
      // 4. Subir backup al bucket S3
      try {
        const uploadResponse = await makeAuthenticatedRequestWithTokenUpdate({
          method: 'POST',
          url: `${api}/api?action=upload_backup`,
          data: {
            filename: filename,
            backup_content: csvContent
          },
          headers: { 'Content-Type': 'application/json' }
        });
        
        setBackupResult({
          success: true,
          message: `${t('admin.backup.success')}: ${filename}`,
          filename: filename,
          count: permissions.length,
          path: backupPath,
          s3Location: `s3://${process.env.VITE_BACKUP_BUCKET || 'default-bucket'}/${backupPath}`,
          uploadDetails: uploadResponse.data
        });
        
        // Actualizar lista de backups disponibles
        await loadAvailableBackups();
        
      } catch (uploadError) {
        console.error('Error subiendo backup a S3:', uploadError);
        console.error('Error details:', uploadError.response?.data);
        
        // Mostrar error específico sin fallback automático
        const errorDetails = uploadError.response?.data?.error || uploadError.message;
        setBackupResult({
          success: false,
          message: `${t('admin.backup.error')}: ${errorDetails}`,
          filename: filename,
          count: permissions.length,
          s3Error: errorDetails,
          suggestion: "Verifique la conectividad con S3 y los permisos del bucket",
          debugInfo: {
            url: uploadError.config?.url,
            status: uploadError.response?.status,
            data: uploadError.response?.data
          }
        });
        
        // NO hacer descarga local automática
        // El usuario puede decidir manualmente si quiere crear una copia local
      }
      
    } catch (error) {
      console.error('Error creando backup:', error);
      setBackupResult({
        success: false,
        message: `${t('admin.backup.error')}: ${error.message}`
      });
    } finally {
      setBackupInProgress(false);
    }
  };
  
  const loadAvailableBackups = async () => {
    setLoadingBackups(true);
    try {
      // Llamar a la API para listar archivos de backup del bucket S3
      const response = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}/api?action=list_backups`,
        headers: { 'Content-Type': 'application/json' }
      });
      
      const backupFiles = response.data.backups || [];
      const formattedBackups = backupFiles.map(file => {
        // Extraer fecha y hora del nombre del archivo
        const dateMatch = file.filename.match(/(\d{4}-\d{2}-\d{2})/);
        const timeMatch = file.filename.match(/(\d{2}-\d{2}-\d{2})/);
        
        return {
          filename: file.filename,
          date: dateMatch ? dateMatch[1] : 'Unknown',
          time: timeMatch ? timeMatch[1].replace(/-/g, ':') : 'Unknown',
          size: formatFileSize(file.size),
          path: file.key,
          lastModified: file.lastModified,
          uploadedBy: file.uploadedBy,
          uploadDate: file.uploadDate,
          backupType: file.backupType
        };
      }).sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
      
      setAvailableBackups(formattedBackups);
      
    } catch (error) {
      console.error('Error cargando backups:', error);
      
      // Fallback: usar datos mock en caso de error
      const now = new Date();
      const mockBackups = [
        {
          filename: `backup_permisos_${now.toISOString().split('T')[0]}_10-30-00.csv`,
          date: now.toISOString().split('T')[0],
          time: '10:30:00',
          size: '3.2 KB',
          path: `backup/${now.toISOString().split('T')[0]}/backup_permisos_${now.toISOString().split('T')[0]}_10-30-00.csv`,
          lastModified: now.toISOString(),
          uploadedBy: 'admin@example.com',
          uploadDate: now.toISOString(),
          backupType: 'permissions'
        }
      ];
      
      setAvailableBackups(mockBackups);
    } finally {
      setLoadingBackups(false);
    }
  };
  
  // Función auxiliar para formatear el tamaño de archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const confirmRestore = (backup) => {
    setSelectedBackup(backup);
    setShowRestoreModal(true);
  };
  
  const executeRestore = async () => {
    if (!selectedBackup) return;
    
    setRestoreInProgress(true);
    setRestoreResult(null);
    setShowRestoreModal(false);
    
    try {
      // 1. Obtener todos los permisos actuales para eliminar
      const currentPermissions = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}/api?action=list_permissions`,
        headers: { 'Content-Type': 'application/json' }
      });
      
      const permissionsToDelete = currentPermissions.data.items || [];
      
      // 2. Eliminar todos los permisos actuales
      let deletedCount = 0;
      const deleteErrors = [];
      
      for (const permission of permissionsToDelete) {
        try {
          await makeAuthenticatedRequestWithTokenUpdate({
            method: 'POST',
            url: `${api}/api?action=delete_permission`,
            data: {
              user_id: permission.user_id,
              path: permission.path
            },
            headers: { 'Content-Type': 'application/json' }
          });
          deletedCount++;
        } catch (error) {
          deleteErrors.push({
            user: permission.user_id,
            path: permission.path,
            error: error.message
          });
        }
      }
      
      // 3. Descargar y procesar el backup desde S3
      let restoredCount = 0;
      const restoreErrors = [];
      
      try {
        // Descargar el backup desde S3
        const backupResponse = await makeAuthenticatedRequestWithTokenUpdate({
          method: 'POST',
          url: `${api}/api?action=download_backup`,
          data: {
            backup_key: selectedBackup.path
          },
          headers: { 'Content-Type': 'application/json' }
        });
        
        const csvContent = backupResponse.data.content;
        
        // 4. Parsear CSV y restaurar permisos
        const lines = csvContent.split('\n');
        const header = lines[0];
        
        // Verificar que el header sea correcto
        if (!header.includes('usuario') || !header.includes('bucket/path') || !header.includes('permiso')) {
          throw new Error('Formato de backup inválido: header incorrecto');
        }
        
        // Procesar cada línea del CSV
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue; // Saltar líneas vacías
          
          try {
            // Parsear CSV con soporte para campos entrecomillados
            const fields = parseCSVLine(line);
            
            if (fields.length < 3) {
              restoreErrors.push({
                line: i + 1,
                content: line,
                error: 'Línea incompleta: faltan campos requeridos'
              });
              continue;
            }
            
            const [usuario, path, permiso, fechaCaducidad = '', notas = ''] = fields;
            
            // Validar campos requeridos
            if (!usuario || !path || !permiso) {
              restoreErrors.push({
                line: i + 1,
                content: line,
                error: 'Campos requeridos vacíos'
              });
              continue;
            }
            
            // Validar nivel de permiso
            if (!['read', 'write'].includes(permiso.toLowerCase())) {
              restoreErrors.push({
                line: i + 1,
                content: line,
                error: `Permiso inválido: ${permiso}`
              });
              continue;
            }
            
            // Formatear path
            let formattedPath = path.trim();
            if (!formattedPath.startsWith('/')) {
              formattedPath = '/' + formattedPath;
            }
            
            // Procesar fecha de caducidad
            let finalExpiryDate = fechaCaducidad.trim() || undefined;
            
            // Si hay fecha de caducidad y está habilitada la extensión automática
            if (finalExpiryDate && extendExpiredPermissions) {
              try {
                const expiryDate = new Date(finalExpiryDate);
                const currentDate = new Date();
                
                // Si la fecha está caducada, extenderla
                if (expiryDate < currentDate) {
                  const newExpiryDate = new Date();
                  newExpiryDate.setDate(newExpiryDate.getDate() + extendDays);
                  finalExpiryDate = newExpiryDate.toISOString().split('T')[0]; // Formato YYYY-MM-DD
                }
              } catch (dateError) {
                // Si hay error parseando la fecha, mantener la original
                console.warn('Error procesando fecha de caducidad:', dateError);
              }
            }
            
            // Crear el permiso
            await makeAuthenticatedRequestWithTokenUpdate({
              method: 'POST',
              url: `${api}/api?action=update_permission`,
              data: {
                user_id: usuario.trim(),
                path: formattedPath,
                level: permiso.toLowerCase(),
                expiry_date: finalExpiryDate,
                notes: notas.trim() || undefined
              },
              headers: { 'Content-Type': 'application/json' }
            });
            
            restoredCount++;
            
          } catch (error) {
            restoreErrors.push({
              line: i + 1,
              content: line,
              error: error.message
            });
          }
        }
        
      } catch (error) {
        throw new Error(`Error procesando backup: ${error.message}`);
      }
      
      // 5. Mostrar resultados
      const success = restoreErrors.length === 0 || restoredCount > 0;
      setRestoreResult({
        success: success,
        message: success 
          ? `Restauración completada desde ${selectedBackup.filename}`
          : `Error en restauración desde ${selectedBackup.filename}`,
        deleted: deletedCount,
        deleteErrors: deleteErrors,
        restored: restoredCount,
        restoreErrors: restoreErrors
      });
      
      // Recargar permisos
      loadPermissions();
      
    } catch (error) {
      console.error('Error en restauración:', error);
      setRestoreResult({
        success: false,
        message: `Error en restauración: ${error.message}`,
        deleted: 0,
        deleteErrors: [],
        restored: 0,
        restoreErrors: []
      });
    } finally {
      setRestoreInProgress(false);
      setSelectedBackup(null);
    }
  };
  
  // Función auxiliar para parsear líneas CSV con campos entrecomillados
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < line.length) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Comilla escapada ""
          current += '"';
          i += 2;
        } else {
          // Inicio o fin de campo entrecomillado
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // Separador de campo
        result.push(current);
        current = '';
        i++;
      } else {
        current += char;
        i++;
      }
    }
    
    // Agregar el último campo
    result.push(current);
    
    return result;
  };
  
  // Cargar backups cuando se abre la sección
  useEffect(() => {
    if (backupSectionOpen && availableBackups.length === 0) {
      loadAvailableBackups();
    }
  }, [backupSectionOpen]);
  
  // ===== FIN FUNCIONES BACKUP =====

  useEffect(() => {
    if (token && isAdmin) {
      loadPermissions();
    }
  }, [token]);

  // Función para descarga local manual (como backup secundario)
  const downloadBackupLocally = async () => {
    try {
      // Regenerar el backup actual para descarga local
      const response = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}/api?action=list_permissions`,
        headers: { 'Content-Type': 'application/json' }
      });
      
      const permissions = response.data.items || [];
      
      if (permissions.length === 0) {
        alert(t('admin.backup.noPermissions'));
        return;
      }
      
      // Generar contenido CSV
      const csvHeader = 'usuario,bucket/path,permiso,fecha_caducidad,notas\n';
      const csvRows = permissions.map(permission => {
        const user = permission.user_id || '';
        const path = permission.path || '';
        const level = permission.level || '';
        const expiry = permission.expiry_date || '';
        const notes = permission.notes || '';
        
        const escapeCSVField = (field) => {
          const fieldStr = String(field);
          if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n') || fieldStr.includes('\r') || fieldStr !== fieldStr.trim()) {
            return `"${fieldStr.replace(/"/g, '""')}"`;
          }
          return fieldStr;
        };
        
        return `${escapeCSVField(user)},${escapeCSVField(path)},${escapeCSVField(level)},${escapeCSVField(expiry)},${escapeCSVField(notes)}`;
      }).join('\n');
      
      const csvContent = csvHeader + csvRows;
      
      // Generar nombre de archivo
      const now = new Date();
      const timestamp = now.toISOString().split('T')[0];
      const timepart = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `backup_permisos_local_${timestamp}_${timepart}.csv`;
      
      // Descargar archivo
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error en descarga local:', error);
      alert(`Error en descarga local: ${error.message}`);
    }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">{t('admin.title')}</h1>

      {/* Sección de Backup y Restauración */}
      <div className="mb-6 p-4 bg-purple-50 rounded-lg">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setBackupSectionOpen(!backupSectionOpen)}
        >
          <h3 className="text-lg font-semibold">💾 {t('admin.backup.title')}</h3>
          <span className="text-2xl font-bold text-purple-600">
            {backupSectionOpen ? '−' : '+'}
          </span>
        </div>
        
        {backupSectionOpen && (
          <div className="mt-4">
            {/* Crear Backup */}
            <div className="mb-6 p-4 bg-purple-100 rounded-md">
              <h4 className="font-semibold text-purple-800 mb-3">📦 {t('admin.backup.createTitle')}</h4>
              <p className="text-sm text-purple-700 mb-4">
                {t('admin.backup.createDescription')}
              </p>
              
              <button
                onClick={createBackup}
                disabled={backupInProgress}
                className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {backupInProgress ? (
                  <>⏳ {t('admin.backup.creating')}</>
                ) : (
                  <>💾 {t('admin.backup.create')}</>
                )}
              </button>

              {/* Resultado del backup */}
              {backupResult && (
                <div className={`mt-4 p-3 rounded-md ${backupResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className={`text-sm ${backupResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    <p className="font-semibold">{backupResult.success ? '✅' : '❌'} {backupResult.message}</p>
                    {backupResult.success && backupResult.count && (
                      <p className="mt-1">📊 {t('admin.backup.backupCount')}: {backupResult.count}</p>
                    )}
                    {backupResult.success && backupResult.s3Location && (
                      <p className="mt-1 text-xs">📍 {t('admin.backup.backupLocation')}: {backupResult.s3Location}</p>
                    )}
                    {!backupResult.success && backupResult.suggestion && (
                      <p className="mt-2 text-xs">💡 {t('admin.backup.connectivityError')}</p>
                    )}
                  </div>
                  
                  {/* Botón para descarga local como backup secundario */}
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <button
                      onClick={downloadBackupLocally}
                      className="text-xs px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                    >
                      💾 {t('admin.backup.downloadLocal')}
                    </button>
                    <p className="text-xs text-gray-600 mt-1">
                      {t('admin.backup.downloadLocalDesc')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Lista de Backups Disponibles */}
            <div className="p-4 bg-blue-50 rounded-md">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-blue-800">📋 {t('admin.backup.available')}</h4>
                <button
                  onClick={loadAvailableBackups}
                  disabled={loadingBackups}
                  className="text-sm px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
                >
                  {loadingBackups ? '⏳' : '🔄'} {t('actions.refresh')}
                </button>
              </div>

              {loadingBackups ? (
                <p className="text-blue-700 text-sm">⏳ {t('admin.backup.loading')}</p>
              ) : availableBackups.length === 0 ? (
                <p className="text-blue-700 text-sm">📭 {t('admin.backup.noBackups')}</p>
              ) : (
                <div className="space-y-2">
                  {availableBackups.map((backup, index) => (
                    <div key={index} className="bg-white p-3 rounded border flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">📄 {backup.filename}</p>
                        <p className="text-sm text-gray-600">
                          📅 {backup.date} ⏰ {backup.time} 📦 {backup.size}
                        </p>
                      </div>
                      <button
                        onClick={() => confirmRestore(backup)}
                        className="ml-4 px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors text-sm"
                      >
                        🔄 {t('admin.backup.restore')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Resultados de Restauración */}
            {restoreResult && (
              <div className={`mt-4 p-4 rounded-md ${restoreResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <h5 className={`font-semibold mb-2 ${restoreResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {restoreResult.success ? '✅' : '❌'} {t('admin.backup.results')}
                </h5>
                <div className="text-sm space-y-1">
                  <p>🗑️ <strong>{t('admin.backup.deleted')}:</strong> {restoreResult.deleted}</p>
                  <p>📥 <strong>{t('admin.backup.restored')}:</strong> {restoreResult.restored}</p>
                  {restoreResult.deleteErrors.length > 0 && (
                    <p className="text-red-600">❌ <strong>{t('admin.backup.deleteErrors')}:</strong> {restoreResult.deleteErrors.length}</p>
                  )}
                  {restoreResult.restoreErrors.length > 0 && (
                    <p className="text-red-600">❌ <strong>{t('admin.backup.restoreErrors')}:</strong> {restoreResult.restoreErrors.length}</p>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-600">{restoreResult.message}</p>
              </div>
            )}

            {/* Indicador de progreso para restauración */}
            {restoreInProgress && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
                  <span className="text-yellow-800 font-medium">⚠️ {t('admin.backup.restoring')}</span>
                </div>
                <p className="text-sm text-yellow-700 mt-2">
                  {t('admin.backup.restoringWarning')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Confirmación para Restauración */}
      {showRestoreModal && selectedBackup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-lg w-full mx-4">
            <h3 className="text-lg font-bold text-red-800 mb-4">
              ⚠️ {t('admin.backup.restoreConfirm')}
            </h3>
            
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
              <p className="text-red-800 font-medium mb-2">
                {t('admin.backup.restoreWarning')}
              </p>
              <p className="text-red-700 text-sm">
                {t('admin.backup.selectedBackup')}: <strong>{selectedBackup.filename}</strong>
              </p>
            </div>
            
            {/* Opciones de restauración */}
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
              <h4 className="font-semibold text-blue-800 mb-3">🛠️ {t('admin.backup.restoreOptions')}</h4>
              
              <div className="space-y-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={extendExpiredPermissions}
                    onChange={(e) => setExtendExpiredPermissions(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-blue-800">
                      {t('admin.backup.extendExpired')}
                    </span>
                    <p className="text-xs text-blue-600 mt-1">
                      {t('admin.backup.extendExpiredDesc')}
                    </p>
                  </div>
                </label>
                
                {extendExpiredPermissions && (
                  <div className="ml-7 flex items-center gap-2">
                    <label className="text-sm text-blue-700">{t('admin.backup.extendDays')}:</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={extendDays}
                      onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                      className="w-20 px-2 py-1 text-sm border border-blue-300 rounded focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-blue-600">{t('admin.backup.days')}</span>
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-gray-700 mb-6">
              {t('admin.backup.restoreQuestion')}
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setSelectedBackup(null);
                  setExtendExpiredPermissions(false);
                  setExtendDays(30);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                {t('admin.backup.restoreCancel')}
              </button>
              <button
                onClick={executeRestore}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors font-medium"
              >
                🔄 {t('admin.backup.restoreExecute')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sección de importación CSV */}
      <div className="mb-6 p-4 bg-green-50 rounded-lg">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setCsvSectionOpen(!csvSectionOpen)}
        >
          <h3 className="text-lg font-semibold">📥 {t('admin.csv.title')}</h3>
          <span className="text-2xl font-bold text-green-600">
            {csvSectionOpen ? '−' : '+'}
          </span>
        </div>
        
        {csvSectionOpen && (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Selección de archivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.csv.file')}
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                />
              </div>

              {/* Modo de operación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.csv.operation')}
                </label>
                <select
                  value={csvMode}
                  onChange={(e) => setCsvMode(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                >
                  <option value="import">{t('admin.csv.import')}</option>
                  <option value="delete">{t('admin.csv.delete')}</option>
                </select>
              </div>
            </div>

            {/* Información sobre el formato */}
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm">
              <h4 className="font-semibold text-yellow-800 mb-2">📋 {t('admin.csv.format')}:</h4>
              <p className="text-yellow-700 mb-2">
                <code>{t('admin.csv.formatDescription')}</code>
              </p>
              <ul className="text-yellow-700 space-y-1 text-xs">
                <li>• <strong>{t('admin.csv.fieldUser')}</strong></li>
                <li>• <strong>{t('admin.csv.fieldPath')}</strong></li>
                <li>• <strong>{t('admin.csv.fieldPermission')}</strong></li>
                <li>• <strong>{t('admin.csv.fieldExpiry')}</strong></li>
                <li>• <strong>{t('admin.csv.fieldNotes')}</strong></li>
              </ul>
            </div>

            {/* Botones de acción */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={downloadCsvTemplate}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
              >
                📄 {t('admin.csv.downloadTemplate')}
              </button>
              
              <button
                onClick={handleCsvImport}
                disabled={!csvFile || csvImporting}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {csvImporting ? (
                  <>⏳ {t('admin.csv.processing')}</>
                ) : (
                  <>📥 {csvMode === 'import' ? t('admin.csv.importButton') : t('admin.csv.deleteButton')}</>
                )}
              </button>
            </div>

            {/* Resultados de la importación */}
            {csvResult && (
              <div className="mt-4 p-3 bg-white border rounded-md">
                <h4 className="font-semibold mb-2">{t('admin.csv.results')}:</h4>
                <div className="text-sm space-y-1">
                  <p>📊 <strong>{t('admin.csv.processed')}:</strong> {csvResult.processed}</p>
                  <p className="text-green-600">✅ <strong>{t('admin.csv.successful')}:</strong> {csvResult.success}</p>
                  <p className="text-red-600">❌ <strong>{t('admin.csv.errors')}:</strong> {csvResult.errors.length}</p>
                </div>
                
                {csvResult.errors.length > 0 && (
                  <div className="mt-3">
                    <h5 className="font-medium text-red-700 mb-2">{t('admin.csv.errorsList')}:</h5>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {csvResult.errors.map((error, index) => (
                        <div key={index} className="text-xs text-red-600 bg-red-50 p-2 rounded">
                          {error.user && error.path ? (
                            <><strong>{error.user}</strong> - {error.path}: {error.error}</>
                          ) : (
                            error.error
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Formulario para agregar permisos */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">{t('admin.add')}</h3>
        
        {/* Ayuda sobre formatos de path */}
        <div className="mb-4 p-3 bg-blue-100 border border-blue-200 rounded-md text-sm">
          <h4 className="font-semibold text-blue-800 mb-2">📝 {t('admin.help.pathFormat')}:</h4>
          <ul className="text-blue-700 space-y-1">
            <li><code>/bucket-name/folder</code> (ej: <code>/uocfiles-itlabs-data/path1/path12</code>)</li>
          </ul>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.userIdPlaceholder')}
            </label>
            <input
              value={userId}
              onChange={e => setUserId(e.target.value)}
              placeholder={t('admin.userIdPlaceholder')}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.pathPlaceholder')}
            </label>
            <input
              value={path}
              onChange={e => setPath(e.target.value)}
              placeholder="bucket/folder o /folder"
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nivel de permiso
            </label>
            <select
              value={level}
              onChange={e => setLevel(e.target.value)}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="read">{t('admin.permissions.read')}</option>
              <option value="write">{t('admin.permissions.write')}</option>
              <option value="admin">{t('admin.permissions.admin')}</option>
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.expiryDate')}
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.notes')}
            </label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={t('admin.notesPlaceholder')}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <button 
          onClick={savePermission} 
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md transition-colors duration-200"
        >
          {t('actions.save')}
        </button>
      </div>

      {/* Filtros */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">{t('filter.title')}</h3>
        
        {/* Filtros básicos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="filter-user" className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.filter.user')}
            </label>
            <input
              id="filter-user"
              type="text"
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              placeholder={t('admin.filter.userPlaceholder')}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="filter-path" className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.filter.path')}
            </label>
            <input
              id="filter-path"
              type="text"
              value={filterPath}
              onChange={(e) => setFilterPath(e.target.value)}
              placeholder={t('admin.filter.pathPlaceholder')}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="filter-expiry" className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.filter.expiry')}
            </label>
            <select
              id="filter-expiry"
              value={filterExpiry}
              onChange={(e) => setFilterExpiry(e.target.value)}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('admin.filter.expiry.all')}</option>
              <option value="expired">{t('admin.filter.expiry.expired')}</option>
              <option value="expiringSoon">{t('admin.filter.expiry.expiringSoon')}</option>
              <option value="valid">{t('admin.filter.expiry.valid')}</option>
            </select>
          </div>
        </div>
        
        {/* Controles de ordenamiento */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="sort-by" className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.sort.by')}
            </label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="user">{t('admin.sort.user')}</option>
              <option value="path">{t('admin.sort.path')}</option>
              <option value="expiry">{t('admin.sort.expiry')}</option>
              <option value="created">{t('admin.sort.created')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="sort-order" className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.sort.order')}
            </label>
            <select
              id="sort-order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="asc">{t('admin.sort.asc')}</option>
              <option value="desc">{t('admin.sort.desc')}</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilterUser('');
                setFilterPath('');
                setFilterExpiry('all');
                setSortBy('user');
                setSortOrder('asc');
              }}
              className="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition-colors duration-200"
            >
              {t('filter.clear')}
            </button>
          </div>
        </div>
        
        {/* Mostrar número de resultados */}
        <div className="mt-3 text-sm text-gray-600">
          {filteredAndSortedEntries.length} {t('filter.results')} 
          {(filterUser || filterPath || filterExpiry !== 'all') && (
            <span className="ml-2 text-gray-500">
              ({t('filter.clear')} aplicado)
            </span>
          )}
        </div>
      </div>

      {/* Lista de permisos */}
      <div>
        <h3 className="text-lg font-semibold mb-3">{t('admin.currentPermissions')}</h3>
        {filteredAndSortedEntries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {entries.length === 0 ? t('admin.noPermissions') : t('filter.noResults')}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAndSortedEntries.map((e, i) => {
              const isExpired = e.expiry_date && new Date(e.expiry_date) < new Date();
              const isExpiringSoon = e.expiry_date && !isExpired && 
                new Date(e.expiry_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 días
              
              return (
                <div
                  key={i}
                  className={`border rounded-lg p-4 transition-all duration-300 ease-in-out animate-fade-in bg-white hover:bg-gray-50 ${
                    isExpired ? 'border-red-300 bg-red-50' : 
                    isExpiringSoon ? 'border-yellow-300 bg-yellow-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="font-semibold text-gray-800">{e.user_id}</div>
                        <span
                          className={`text-xs px-3 py-1 rounded-full font-medium ${
                            e.level === 'read'
                              ? 'bg-blue-100 text-blue-700'
                              : e.level === 'write'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {t(`admin.permissions.${e.level}`)}
                        </span>
                        {isExpired && (
                          <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                            {t('admin.status.expired')}
                          </span>
                        )}
                        {isExpiringSoon && !isExpired && (
                          <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                            {t('admin.status.expiringSoon')}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>{t('admin.pathPlaceholder')}:</strong> {e.path}
                      </div>
                      
                      {e.created_date && (
                        <div className="text-xs text-gray-500 mb-1">
                          <strong>{t('admin.created')}:</strong> {new Date(e.created_date).toLocaleDateString('es-ES')}
                        </div>
                      )}
                      
                      {e.expiry_date && (
                        <div className={`text-xs mb-1 ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : 'text-gray-500'}`}>
                          <strong>{t('admin.expires')}:</strong> 
                          <button
                            onClick={() => startEditPermission(e)}
                            className="ml-1 hover:underline cursor-pointer"
                            title={t('admin.edit.clickToEdit')}
                          >
                            {new Date(e.expiry_date).toLocaleDateString('es-ES')}
                          </button>
                        </div>
                      )}
                      
                      {e.notes && (
                        <div className="text-xs text-gray-600 italic mt-2 p-2 bg-gray-100 rounded">
                          <strong>{t('admin.list.notes')}:</strong> 
                          <button
                            onClick={() => startEditPermission(e)}
                            className="ml-1 hover:underline cursor-pointer"
                            title={t('admin.edit.clickToEdit')}
                          >
                            {e.notes}
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <button 
                      onClick={() => deletePermission(e.user_id, e.path)} 
                      className="text-red-600 hover:text-red-800 p-2 rounded-md hover:bg-red-50 transition-colors duration-200 ml-4"
                      title={t('actions.delete')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Edición inline */}
                  {editingPermission === `${e.user_id}-${e.path}` && (
                    <div className="mt-4 p-3 bg-gray-100 rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('admin.expiryDate')}
                          </label>
                          <input
                            type="date"
                            value={editExpiryDate}
                            onChange={e => setEditExpiryDate(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('admin.notes')}
                          </label>
                          <input
                            value={editNotes}
                            onChange={e => setEditNotes(e.target.value)}
                            placeholder={t('admin.notesPlaceholder')}
                            className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <button 
                          onClick={() => editPermission(e.user_id, e.path, editExpiryDate, editNotes)} 
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors duration-200"
                        >
                          {t('actions.save')}
                        </button>
                        <button 
                          onClick={cancelEdit} 
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition-colors duration-200"
                        >
                          {t('actions.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Botón de editar */}
                  {!editingPermission && (
                    <button 
                      onClick={() => startEditPermission(e)} 
                      className="text-blue-600 hover:text-blue-800 mt-2"
                    >
                      {t('actions.edit')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
